import { Component, Inject, OnInit, Pipe, PipeTransform, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { HtmlEditorComponent } from 'src/app/shared/html-editor/html-editor.component'; // Adjust path as needed

export interface EditMessageDialogData {
  type: 'sms' | 'email';
  typeToken: string;
  defaultMessage: string;
  defaultSubject?: string;
  link: string;
}

export interface EditMessageDialogResult {
  message: string;
  subject?: string;
  confirmed: boolean;
}

@Pipe({
  name: 'truncateLink',
  standalone: true
})
export class TruncateLinkPipe implements PipeTransform {
  transform(value: string, maxLength: number = 50): string {
    if (!value || value.length <= maxLength) return value;
    return value.substring(0, maxLength) + '...';
  }
}

@Component({
  selector: 'app-edit-message-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    HtmlEditorComponent,
    TruncateLinkPipe
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div class="edit-message-dialog">
      <h2 mat-dialog-title class="dialog-title">
        <mat-icon class="title-icon">{{ data.type === 'sms' ? 'sms' : 'email' }}</mat-icon>
        {{ data.type === 'sms' ? 'Edit SMS Message' : 'Edit Email' }}
      </h2>
      
      <mat-dialog-content>
        @if (data.type === 'sms') {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Message</mat-label>
            <textarea
              matInput
              [(ngModel)]="smsMessage"
              rows="5"
              placeholder="Enter your message..."
            ></textarea>
            <mat-hint align="end">{{ smsMessage.length }} characters</mat-hint>
          </mat-form-field>
          
          <div class="link-info">
            <mat-icon>link</mat-icon>
            <span>Link included: <code>{{ data.link | truncateLink }}</code></span>
          </div>
        }
        
        @if (data.type === 'email') {
          <mat-form-field appearance="outline" class="full-width subject-field">
            <mat-label>Subject</mat-label>
            <input matInput [(ngModel)]="emailSubject" placeholder="Email subject..." />
          </mat-form-field>
          
          <div class="editor-container">
            <label class="editor-label">Message Body</label>
            <app-html-editor
              [formControl]="emailBodyControl"
              [height]="'250px'"
              [placeholder]="'Enter your email message...'"
            ></app-html-editor>
          </div>
        }
      </mat-dialog-content>
      
      <mat-dialog-actions align="end">
        <button mat-button class="cancel-btn" (click)="onCancel()">Cancel</button>
        <button 
          mat-flat-button 
          class="send-btn"
          (click)="onSend()"
          [disabled]="!isValid()"
        >
          <mat-icon>send</mat-icon>
          Send {{ data.type === 'sms' ? 'SMS' : 'Email' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    /* Dialog Container Override */
    .cdk-overlay-pane:has(.edit-message-dialog) {
      .mat-mdc-dialog-container {
        --mdc-dialog-container-color: var(--lead-bg-primary);
        --mdc-dialog-container-shape: 12px;
        
        .mdc-dialog__surface {
          background-color: var(--lead-bg-primary) !important;
          border-radius: 12px !important;
          box-shadow: var(--lead-shadow-hover) !important;
          overflow: hidden;
        }
      }
    }

    .edit-message-dialog {
      background-color: var(--lead-bg-primary);
      color: var(--lead-text-primary);
      font-family: 'Inter', 'Segoe UI', sans-serif;

      /* Dialog Title */
      .dialog-title {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0;
        padding: 16px 24px;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--lead-text-primary);
        border-bottom: 1px solid var(--lead-border-color);
        background-color: var(--lead-bg-secondary);
      }

      .title-icon {
        color: var(--lead-info-color);
        font-size: 24px;
        width: 24px;
        height: 24px;
      }

      /* Dialog Content */
      mat-dialog-content {
        min-width: 450px;
        max-width: 600px;
        padding: 20px 24px !important;
        background-color: var(--lead-bg-primary);
      }

      .full-width {
        width: 100%;
      }

      .subject-field {
        margin-bottom: 16px;
      }

      /* Link Info Box */
      .link-info {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 14px 16px;
        background-color: var(--lead-bg-tertiary);
        border: 1px solid var(--lead-border-color);
        border-radius: 8px;
        margin-top: 16px;
        font-size: 0.85rem;
        color: var(--lead-text-secondary);

        mat-icon {
          font-size: 20px;
          width: 20px;
          height: 20px;
          color: var(--lead-info-color);
        }

        code {
          background-color: var(--lead-hover-bg);
          color: var(--lead-info-color);
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
          word-break: break-all;
          font-family: 'Monaco', 'Consolas', monospace;
        }
      }

      /* Editor Container */
      .editor-container {
        margin-top: 8px;
      }

      .editor-label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--lead-text-secondary);
        margin-bottom: 10px;
        display: block;
      }

      /* Dialog Actions */
      mat-dialog-actions {
        padding: 16px 24px !important;
        margin: 0 !important;
        border-top: 1px solid var(--lead-border-color);
        background-color: var(--lead-bg-secondary);
        gap: 12px;
      }

      /* Cancel Button */
      .cancel-btn {
        color: var(--lead-text-secondary);
        background-color: transparent;
        border: 1px solid var(--lead-border-color);
        border-radius: 6px;
        padding: 0 20px;
        height: 40px;
        font-weight: 500;
        transition: all 0.2s ease;

        &:hover {
          background-color: var(--lead-hover-bg);
          color: var(--lead-text-primary);
          border-color: var(--lead-text-secondary);
        }
      }

      /* Send Button */
      .send-btn {
        background-color: var(--lead-info-color) !important;
        color: white !important;
        border-radius: 6px;
        padding: 0 20px;
        height: 40px;
        font-weight: 500;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 6px;

        &:hover:not([disabled]) {
          background-color: var(--lead-accent-hover) !important;
          box-shadow: var(--lead-shadow-hover);
        }

        &:disabled {
          background-color: var(--lead-text-muted) !important;
          opacity: 0.6;
          cursor: not-allowed;
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }

      /* Material Form Field Overrides */
      .mat-mdc-form-field {
        .mat-mdc-text-field-wrapper {
          background-color: var(--lead-bg-secondary) !important;
        }

        .mat-mdc-form-field-focus-overlay {
          background-color: transparent !important;
        }

        .mdc-notched-outline__leading,
        .mdc-notched-outline__notch,
        .mdc-notched-outline__trailing {
          border-color: var(--lead-border-color) !important;
        }

        &.mat-focused .mdc-notched-outline__leading,
        &.mat-focused .mdc-notched-outline__notch,
        &.mat-focused .mdc-notched-outline__trailing {
          border-color: var(--lead-info-color) !important;
        }

        .mat-mdc-form-field-label,
        .mdc-floating-label {
          color: var(--lead-text-secondary) !important;
        }

        &.mat-focused .mat-mdc-form-field-label,
        &.mat-focused .mdc-floating-label {
          color: var(--lead-info-color) !important;
        }

        .mat-mdc-input-element {
          color: var(--lead-text-primary) !important;
          caret-color: var(--lead-info-color);
        }

        .mat-mdc-form-field-hint {
          color: var(--lead-text-muted);
        }

        textarea.mat-mdc-input-element {
          resize: vertical;
          min-height: 100px;
        }
      }

      /* Responsive */
      @media (max-width: 600px) {
        mat-dialog-content {
          min-width: 300px;
          padding: 16px !important;
        }

        .dialog-title {
          padding: 12px 16px;
          font-size: 1.1rem;
        }

        mat-dialog-actions {
          padding: 12px 16px !important;
          flex-direction: column;
        }

        .cancel-btn,
        .send-btn {
          width: 100%;
          justify-content: center;
        }
      }
    }
  `]
})
export class EditMessageDialogComponent implements OnInit {
  smsMessage: string = '';
  emailSubject: string = '';
  emailBodyControl = new FormControl('', Validators.required);

  constructor(
    public dialogRef: MatDialogRef<EditMessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditMessageDialogData
  ) {}

  ngOnInit(): void {
    if (this.data.type === 'sms') {
      this.smsMessage = this.data.defaultMessage;
    } else {
      this.emailSubject = this.data.defaultSubject || '';
      this.emailBodyControl.setValue(this.data.defaultMessage);
    }
  }

  isValid(): boolean {
    if (this.data.type === 'sms') {
      return this.smsMessage.trim().length > 0;
    } else {
      return this.emailSubject.trim().length > 0 && 
             (this.emailBodyControl.value?.trim().length ?? 0) > 0;
    }
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }

  onSend(): void {
    if (this.data.type === 'sms') {
      this.dialogRef.close({
        message: this.smsMessage,
        confirmed: true
      } as EditMessageDialogResult);
    } else {
      this.dialogRef.close({
        message: this.emailBodyControl.value,
        subject: this.emailSubject,
        confirmed: true
      } as EditMessageDialogResult);
    }
  }
}